import { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { EstimateStatus, EstimateStatuses, ProjectStatus, ProjectStatuses, ProjectType, ProjectTypes } from '../../common/constants'
import { useDebouncedState } from '../../hooks/useDebouncedState'
import { useSavedState } from '../../hooks/useSavedState'
import { jobListSettingKey } from './jobListSettings'

export const ALL_PROJECT_TYPES = 99 as const
export type EstimateProjectType = ProjectType | typeof ALL_PROJECT_TYPES

export const convertEstimateProjectType = (type: EstimateProjectType): ProjectType | undefined =>
  type === ALL_PROJECT_TYPES ? undefined : type

const allProjectStatuses = [
  ProjectStatuses.Unscheduled,
  ProjectStatuses.Scheduled,
  ProjectStatuses.WaitingOnParts,
  ProjectStatuses.Completed
]

const allEstimateStatuses = [
  EstimateStatuses.Unsent,
  EstimateStatuses.Sent,
  EstimateStatuses.Approved,
  EstimateStatuses.Lost
]

type Options = {
  jobType?: ProjectType
  deleted?: boolean
  sold?: boolean
}

export type JobListFilterState = ReturnType<typeof useJobListFilters>

export const useJobListFilters = ({ jobType, deleted = false, sold = true }: Options) => {
  const { debouncedState: debouncedSearchText, state: searchText, setState: setSearchText } =
    useDebouncedState<string | undefined>(undefined, 200)
  const [selectedStatuses, setSelectedStatuses] = useSavedState<ProjectStatus[]>(`job-list/${jobType}/selected-statuses`, allProjectStatuses)
  const [selectedEstimateStatuses, setSelectedEstimateStatuses] = useSavedState<EstimateStatus[]>(`job-list/estimates/selected-statuses`, allEstimateStatuses)
  const [estimateJobType, setEstimateJobType] = useSavedState<EstimateProjectType>('job-list/estimates/job-type', ALL_PROJECT_TYPES)

  const settingKey = (setting: string) => jobListSettingKey({ jobType, deleted }, setting)
  const [startDate, setStartDate] = useSavedState<Dayjs | null>(settingKey('start-date'), null, s => dayjs(s))
  const [endDate, setEndDate] = useSavedState<Dayjs | null>(settingKey('end-date'), null, s => dayjs(s))
  const [sortColumn, setSortColumn] = useSavedState<string | undefined>(settingKey('sort-column'), sold ? 'soldDate' : 'estimateDateUtc')
  const [sortOrder, setSortOrder] = useSavedState<'ascend' | 'descend' | null | undefined>(settingKey('sort-order'), 'descend')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useSavedState(settingKey('page-size'), 10)
  const [archived, setArchived] = useSavedState<boolean>(settingKey('archived'), false)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearchText, selectedStatuses, startDate, endDate, sortColumn, sortOrder, archived])

  const anyFiltersSet = !!debouncedSearchText || !!startDate || !!endDate || archived
    || selectedStatuses.length !== 4 || selectedEstimateStatuses.length !== 4

  const clearFilters = () => {
    setSearchText(undefined)
    setStartDate(null)
    setEndDate(null)
    setSelectedStatuses(allProjectStatuses)
    setSelectedEstimateStatuses(allEstimateStatuses)
    setArchived(false)
  }

  const request = {
    sortColumn,
    sortOrder: sortOrder ?? undefined,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
    statuses: selectedStatuses,
    estimateStatuses: selectedEstimateStatuses,
    search: debouncedSearchText,
    jobType: sold ? jobType : convertEstimateProjectType(estimateJobType),
    sold,
    archived
  }

  return {
    searchText, setSearchText, debouncedSearchText,
    selectedStatuses, setSelectedStatuses,
    selectedEstimateStatuses, setSelectedEstimateStatuses,
    estimateJobType, setEstimateJobType,
    startDate, setStartDate,
    endDate, setEndDate,
    sortColumn, setSortColumn,
    sortOrder, setSortOrder,
    page, setPage,
    pageSize, setPageSize,
    archived, setArchived,
    anyFiltersSet, clearFilters,
    request,
    jobType, deleted, sold
  }
}

export const buildProjectTypeOptions = (showServiceTickets: boolean) => [
  { label: 'Projects', value: ProjectTypes.Project },
  ...(showServiceTickets ? [{ label: 'Service Tickets', value: ProjectTypes.Ticket }] : []),
  { label: 'Maintenance', value: ProjectTypes.Maintenance },
  { label: 'All', value: ALL_PROJECT_TYPES }
]
